/**
 * API Client for NodeTemplates
 *
 * Implements a dual-source strategy:
 * 1. PRIMARY: Backend API endpoints (BaseTemplateDataAssetController)
 * 2. FALLBACK: Static JSON files (for development/offline support) — DISABLED, see below
 *
 * Endpoints:
 * - POST /api/v1/ai/template/assets/GetNodeTemplates        (DataTemplateTypeID = 13)
 * - POST /api/v1/ai/template/assets/GetNodeTypeTemplates    (DataTemplateTypeID = 14)
 * - POST /api/v1/ai/template/assets/GetNodeCategoryTemplates (DataTemplateTypeID = 15)
 *
 * Each endpoint returns DataTemplate rows. The node/category JSON payload is in
 * the ContentData field and must be JSON.parsed to get the typed object.
 */

import { calculateApiUrl } from '@bizfirst/common-js';
import type { NodeType } from '../Domain/NodeType';
import type { NodeCategory } from '../Domain/NodeCategory';
import type { NodeTemplate } from '../Domain/NodeTemplate';
import {
  emitFetchStarted,
  emitFetchCompleted,
  emitFetchFailed,
} from '../Events/NodeTemplateEvents';

// Import from DataTemplates module and re-export so callers can import from one place
import type { DataTemplateRecord } from '../../DataTemplates/types';
export type { DataTemplateRecord };

/**
 * Response format from backend API
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T[];
  error?: {
    code: string;
    message: string;
  };
  errors?: Array<{
    code: string;
    message: string;
  }>;
}

function parseContentData<T>(record: DataTemplateRecord): T | null {
  if (!record.contentData) return null;
  try {
    return JSON.parse(record.contentData) as T;
  } catch (error) {
    const errorCode = 'Error-FlowStudio-100090';
    const errorMsg = error instanceof Error ? error.message : String(error);
    const severity = 'CRITICAL';

    // Extract position from error message (e.g., "Unexpected token at position 245")
    const positionMatch = errorMsg.match(/position (\d+)/) || errorMsg.match(/line (\d+)/);
    const problemPosition = positionMatch ? positionMatch[1] : 'unknown';

    // Show context around the problem area
    const contextStart = Math.max(0, Number(problemPosition) - 50);
    const contextEnd = Math.min(record.contentData?.length || 0, Number(problemPosition) + 50);
    const context = record.contentData?.substring(contextStart, contextEnd) || '';

    // Agent hints for debugging
    const agentHints = [
      '💡 AGENT HINTS FOR DEBUGGING:',
      '1. Check if "category" property is missing a comma after its value',
      '2. Look for unescaped quotes (\\") in string values',
      '3. Verify all opening braces/brackets have corresponding closing ones',
      '4. Check for trailing commas after the last object property',
      '5. Ensure the SQL file uses N\'...\' notation for Unicode strings',
      '6. Extract the JSON and validate at https://jsonlint.com',
      '',
      'QUICK FIX: Run: cat "C:\\...\\Template_DataTemplates_' + record.dataTemplateID + '*.data.sql" | jq .',
    ].join('\n');

    console.error(
      `[${errorCode}] ${severity}: Failed to parse ContentData for template ID ${record.dataTemplateID}\n` +
      `Template Name: ${record.templateName || 'Unknown'}\n` +
      `JSON Parse Error: ${errorMsg}\n` +
      `Problem Position: ${problemPosition}\n` +
      `Context: ...${context}...\n` +
      `\n${agentHints}\n` +
      `📖 Reference: https://docs.BizFirst.com/WebSites/ErrorManagement/ErrorCodes/Error-FlowStudio-100090.html\n` +
      `🗂️  Data File Path: C:\\BizFirstGO_FI_AI\\BizFirstFiDB\\[DB]\\dbo\\Data\\projects\\[Project]\\DataTemplates\\Template_DataTemplates_${record.dataTemplateID}_*.data.sql`
    );
    return null;
  }
}

/**
 * Parse a DataTemplate row and stamp its dataTemplateID onto the `id` field.
 * All three entity types (NodeType, NodeTemplate, NodeCategory) originate from
 * DataTemplates rows and must carry a unique id — log an error if the row lacks one.
 */
function parseRow<T extends { id?: number }>(row: DataTemplateRecord, entityType: string): T | null {
  if (!row.dataTemplateID) {
    console.error(`[NodeTemplate] ${entityType} row missing dataTemplateID — record will have no unique id`);
  }
  const parsed = parseContentData<T>(row);
  if (!parsed) return null;
  if (!parsed.id && row.dataTemplateID) parsed.id = row.dataTemplateID;
  return parsed;
}

function parseRows<T extends { id?: number }>(rows: DataTemplateRecord[], entityType: string): T[] {
  return rows
    .map(row => parseRow<T>(row, entityType))
    .filter((item): item is T => item !== null);
}

export class NodeTemplateApiClient {
  // Uses /api/v1/ai/template/data-template/by-type (registered endpoint)
  // /api/v1/ai/template/assets/* does not exist in the running server
  // Absolute URL built from VITE_API_BASE_URL (same resolution as flow-studio-api's apiConfig) so
  // builds served from a static host (IIS) reach the API directly instead of the page origin.
  private readonly byTypeUrl = `${calculateApiUrl(import.meta.env.VITE_API_BASE_URL).replace(/\/$/, '')}/api/v1/ai/template/data-template/by-type`;
  private readonly API_TIMEOUT = 300000;

  /**
   * Build by-type search request matching the registered endpoint contract
   */
  private buildByTypeRequest(typeId: number): object {
    return {
      metadata: {
        requestId: this.generateId(),
        correlationId: this.generateId(),
        timestamp: new Date().toISOString(),
        version: '1.0',
      },
      DataTemplateTypeID: { ID: typeId },
      PageNumber: 1,
      PageSize: 10000,
    };
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Fetch DataTemplate records by TypeID from the registered by-type endpoint.
   */
  private async fetchByType<T>(typeId: number, entityName: string): Promise<T[]> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.API_TIMEOUT);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Tenant-ID': '1',
    };
    const token = localStorage.getItem('authToken');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await fetch(this.byTypeUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(this.buildByTypeRequest(typeId)),
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      throw new Error(
        `[NodeTemplate] by-type TypeID=${typeId} (${entityName}) failed: HTTP ${response.status}`
      );
    }

    const apiResponse: ApiResponse<T> = await response.json();

    if (!apiResponse.success || !apiResponse.data) {
      throw new Error(
        `[NodeTemplate] by-type TypeID=${typeId} returned error: ${apiResponse.error?.message ?? 'no data'}`
      );
    }

    return apiResponse.data;
  }

  // /**
  //  * Fetch from JSON file (fallback) — DISABLED
  //  */
  // private async fetchFromJson<T>(
  //   filename: string,
  //   entityName: string
  // ): Promise<T[]> {
  //   const response = await fetch(`${this.jsonBasePath}/${filename}?t=${Date.now()}`);
  //   if (!response.ok) {
  //     throw new Error(`Failed to fetch ${entityName}: ${response.statusText}`);
  //   }
  //   const data: T[] = await response.json();
  //   return data;
  // }

  /**
   * Fetch node categories.
   * Throws if the API call fails.
   */
  async fetchNodeCategories(): Promise<NodeCategory[]> {
    emitFetchStarted('NodeCategory');
    try {
      const apiData = await this.fetchByType<DataTemplateRecord>(15, 'Node Categories');
      const parsed = parseRows<NodeCategory>(apiData, 'NodeCategory');
      emitFetchCompleted('NodeCategory', parsed);
      return parsed;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('[NodeTemplate] Failed to fetch node categories:', err);
      emitFetchFailed('NodeCategory', err);
      throw err;
    }
  }

  /**
   * Fetch all data in parallel using /api/v1/ai/template/data-template/by-type.
   * TypeID 13 → templates (individual nodes shown in palette)
   * TypeID 14 → nodeTypes (NodeType definitions for enhancer)
   * TypeID 15 → nodeCategories
   */
  async fetchAll(): Promise<{
    nodeTypes: NodeType[];
    nodeCategories: NodeCategory[];
    templates: NodeTemplate[];
  }> {
    emitFetchStarted('NodeType');
    emitFetchStarted('NodeTemplate');

    const [templateRecords, nodeTypeRecords, nodeCategories] = await Promise.all([
      this.fetchByType<DataTemplateRecord>(13, 'Node Templates'),
      this.fetchByType<DataTemplateRecord>(14, 'Node Types'),
      this.fetchNodeCategories(),
    ]);

    const nodeTypes = parseRows<NodeType>(nodeTypeRecords, 'NodeType');
    const templates = parseRows<NodeTemplate>(templateRecords, 'NodeTemplate');

    emitFetchCompleted('NodeType', nodeTypes);
    emitFetchCompleted('NodeTemplate', templates);

    return { nodeTypes, nodeCategories, templates };
  }

  /**
   * Fetch raw DataTemplate records for a given type (unparsed).
   */
  async fetchRawRecords(typeId: 13 | 14 | 15): Promise<DataTemplateRecord[]> {
    return this.fetchByType<DataTemplateRecord>(typeId, `Type${typeId}`);
  }

  /**
   * Enable/disable API usage (for debugging) — no-op, JSON fallback is disabled
   */
  // public setUseApi(_useApi: boolean): void { }
}
